import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { resendReceipt } from '@/api/bursar';
import { DataTable, Filters, KV, Modal, Notice, PageHeader, Pager, Search, dt, errMsg, inputCls, money, useAction, useGet, useList } from '@/features/shared/ops/kit';

export function ReceiptModal({ id, onClose }: { id: number | string; onClose: () => void }) {
  const q = useGet<any>('receipt', `/school/receipts/${id}`);
  const a = useAction();
  const [phone, setPhone] = useState('');
  const r = q.data;
  return (
    <Modal title={r ? `Receipt ${r.number}` : 'Receipt'} onClose={onClose}>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {a.banner}
      {r && (
        <div className="space-y-3 text-sm" id="receipt-print">
          {r.void && <Notice kind="error">This receipt is void: the payment was reversed.</Notice>}
          <div className="text-center">
            <p className="font-bold">{r.school?.name}</p>
            <p className="text-slate-500">{[r.school?.address, r.school?.phone].filter(Boolean).join(' · ')}</p>
          </div>
          <KV k="Issued" v={dt(r.issued_at)} />
          <KV k="Student" v={r.student ? `${r.student.name} (${r.student.admission_no})` : 'Unassigned'} />
          <KV k="Reference" v={r.payment?.reference} />
          <KV k="Method" v={r.payment?.method} />
          <KV k="Payer" v={r.payment?.payer_name ?? '—'} />
          <KV k="Status" v={<StatusBadge status={r.payment?.status} />} />
          <DataTable rows={r.lines ?? []} empty="Not allocated to an invoice yet." cols={[{ head: 'Invoice', cell: (l: any) => l.invoice_no }, { head: 'Paid', right: true, cell: (l: any) => money(l.amount) }, { head: 'Balance', right: true, cell: (l: any) => money(l.invoice_balance) }]} />
          <p className="text-right text-base font-bold">Total {money(r.payment?.amount)}</p>
          <div className="flex gap-2 items-end flex-wrap">
            <label className="flex-1 min-w-[10rem]">SMS number (optional)<input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Defaults to parent's phone" /></label>
            <Button variant="ghost" disabled={a.busy || r.void} onClick={() => a.run(async () => (await resendReceipt(id, phone || undefined)))}>Resend SMS</Button>
            <Button onClick={() => window.print()}>Print</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Resolve a payment to its receipt, then show it. */
export function PaymentReceipt({ paymentId, onClose }: { paymentId: number; onClose: () => void }) {
  const q = useGet<any>('payment-receipt', `/school/payments/${paymentId}`);
  if (q.isLoading) return <Modal title="Receipt" onClose={onClose}><p className="text-sm text-slate-500">Loading…</p></Modal>;
  if (q.error || !q.data?.receipt_id) return <Modal title="Receipt" onClose={onClose}><Notice kind="info">{q.error ? errMsg(q.error) : 'No receipt has been issued for this payment yet.'}</Notice></Modal>;
  return <ReceiptModal id={q.data.receipt_id} onClose={onClose} />;
}

export function ReceiptsPage() {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const list = useList('receipts-src', '/school/payments', { status: 'successful', q, from, to });
  const rows = list.rows.filter((r: any) => r.receipt_no);
  return (
    <div>
      <PageHeader title="Receipts" sub="Every successful payment has a receipt. Open one to view, print or resend it." />
      <Card>
        <Filters>
          <Search value={q} onChange={setQ} placeholder="Reference or payer name" />
          <label className="text-xs text-slate-500">From<input type="date" className="block border rounded-xl px-3 py-2 text-sm bg-white text-slate-900" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="text-xs text-slate-500">To<input type="date" className="block border rounded-xl px-3 py-2 text-sm bg-white text-slate-900" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </Filters>
        <DataTable
          rows={rows}
          loading={list.isLoading}
          error={list.error}
          empty="No receipts found."
          onRow={(r: any) => setOpen(r.id)}
          cols={[
            { head: 'Receipt', cell: (r: any) => r.receipt_no },
            { head: 'Student', cell: (r: any) => (r.student ? `${r.student} · ${r.admission_no ?? ''}` : r.payer_name ?? 'Unassigned') },
            { head: 'Method', cell: (r: any) => r.method },
            { head: 'Date', cell: (r: any) => dt(r.paid_at) },
            { head: 'Amount', right: true, cell: (r: any) => money(r.amount) },
          ]}
        />
        <Pager meta={list.meta} page={list.page} onPage={list.setPage} />
      </Card>
      {open != null && <PaymentReceipt paymentId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
